import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import Brand from "./Brand";
import { ChevronDown, Menu, X } from "lucide-react";

const INDUSTRIES = [
  { to: "/industries/agriculture", label: "Agricultural Quality Control" },
  { to: "/industries/food-distribution", label: "Food Distribution" },
  { to: "/industries/restaurants", label: "Restaurants & Hospitality" },
  { to: "/industries/supply-chain", label: "Supply-Chain Monitoring" },
];

export default function Navbar({ onRequestDemo }) {
  const [scrolled, setScrolled] = useState(false);
  const [mobile, setMobile] = useState(false);
  const [openFeatures, setOpenFeatures] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobile(false);
    setOpenFeatures(false);
  }, [location]);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-[rgba(3,8,5,0.92)] border-b border-[rgba(157,252,4,0.18)] backdrop-blur-md"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 h-20 flex items-center justify-between">
        <Link to="/" data-testid="nav-home-brand">
          <Brand size="nav" />
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-2" aria-label="Primary">
          <NavLink
            to="/"
            end
            className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}
            data-testid="nav-home"
          >
            Home
          </NavLink>
          <div className="nav-item" data-testid="nav-features-wrap">
            <button
              type="button"
              className="nav-link flex items-center gap-1"
              data-testid="nav-features"
              aria-haspopup="true"
            >
              Features <ChevronDown size={14} strokeWidth={2.5} />
            </button>
            <div className="nav-dd" role="menu">
              <div className="dd-heading">FOOD &amp; AGRICULTURAL APPLICATIONS</div>
              {INDUSTRIES.map((f) => (
                <Link
                  key={f.to}
                  to={f.to}
                  className="dd-link"
                  data-testid={`nav-features-${f.to.split("/").pop()}`}
                >
                  {f.label}
                </Link>
              ))}
            </div>
          </div>
          <NavLink to="/about" className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`} data-testid="nav-about">
            About
          </NavLink>
          <NavLink to="/contact" className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`} data-testid="nav-contact">
            Contact
          </NavLink>
        </nav>

        <div className="hidden lg:block">
          <button type="button" className="btn-ghost" onClick={onRequestDemo} data-testid="nav-request-demo">
            Request Demo
          </button>
        </div>

        {/* Mobile toggle */}
        <button
          type="button"
          className="lg:hidden text-[color:var(--ss-green)]"
          onClick={() => setMobile((v) => !v)}
          aria-label="Toggle menu"
          data-testid="nav-mobile-toggle"
        >
          {mobile ? <X size={28} /> : <Menu size={28} />}
        </button>
      </div>

      {/* Mobile menu */}
      {mobile && (
        <div
          className="lg:hidden bg-[rgba(3,8,5,0.98)] border-t border-[rgba(157,252,4,0.18)] backdrop-blur-md"
          data-testid="nav-mobile-menu"
        >
          <div className="px-6 py-4 flex flex-col gap-1">
            <Link to="/" className="nav-link py-2">Home</Link>
            <button
              type="button"
              className="nav-link flex items-center justify-between py-2"
              onClick={() => setOpenFeatures((v) => !v)}
            >
              Features <ChevronDown size={14} />
            </button>
            {openFeatures && (
              <div className="pl-4 flex flex-col">
                <div className="dd-heading mt-1">FOOD &amp; AGRICULTURAL APPLICATIONS</div>
                {INDUSTRIES.map((f) => (
                  <Link key={f.to} to={f.to} className="dd-link">
                    {f.label}
                  </Link>
                ))}
              </div>
            )}
            <Link to="/about" className="nav-link py-2">About</Link>
            <Link to="/contact" className="nav-link py-2">Contact</Link>
            <button type="button" className="btn-primary mt-4" onClick={onRequestDemo}>
              Request Demo
            </button>
          </div>
        </div>
      )}
    </header>
  );
}
